import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Layers, 
  ArrowUp, 
  ArrowDown, 
  Eye, 
  EyeOff, 
  Smartphone, 
  Monitor, 
  Save, 
  Sparkles, 
  CheckCircle2, 
  Megaphone, 
  Palette, 
  ShoppingBag, 
  Flame, 
  Leaf, 
  CreditCard, 
  Sliders, 
  RotateCcw,
  Check,
  Zap,
  ExternalLink,
  ShieldCheck,
  Plus,
  Trash2,
  Edit3,
  SlidersHorizontal,
  ChevronRight,
  TrendingUp,
  Tag,
  Gift,
  Clock,
  Bookmark
} from 'lucide-react';
import { Modal } from '../common/Modal';
import { AdminApiClient } from '../../services/adminApiClient';

export type SectionElementType = 
  | 'hero_banner_carousel'
  | 'category_showcase'
  | 'product_list_category'
  | 'trending_products'
  | 'sponsored_products'
  | 'products_on_sales'
  | 'recommended_products'
  | 'latest_products'
  | 'deals_of_the_day'
  | 'side_ad'
  | 'carbon_ticker';

export interface LandingSectionConfig {
  id: string;
  name: string;
  type: SectionElementType;
  selectedCategory?: string; // For product_list_category
  enabled: boolean;
  order: number;
  title: string;
  subtitle?: string;
  badgeText?: string;
  // Background customization
  bgType: 'default' | 'solid' | 'gradient';
  bgColor: string; // hex code or css gradient string
  // Display layout
  displayDesktop: 'grid' | 'carousel';
  displayMobile: 'grid' | 'carousel';
  columnsDesktop: number; // 1, 2, 3, 4, 6
  columnsMobile: number; // 1, 2
  // See All button customization
  showSeeAll: boolean;
  seeAllText: string;
  seeAllUrl: string;
}

export const ELEMENT_TYPE_OPTIONS: { id: SectionElementType; label: string; icon: any; desc: string }[] = [
  { id: 'hero_banner_carousel', label: 'Hero Responsive Banner Carousel', icon: Megaphone, desc: 'Full-width or split carousel with marketing banners' },
  { id: 'category_showcase', label: 'Product Category Showcase (Pastel)', icon: Palette, desc: 'Vibrant pastel colored tiles for popular item categories' },
  { id: 'product_list_category', label: 'Product List by Selected Category', icon: ShoppingBag, desc: 'Filtered shelf of products from a specified department' },
  { id: 'trending_products', label: 'Trending Products', icon: TrendingUp, desc: 'Most viewed and rapidly selling items this week' },
  { id: 'sponsored_products', label: 'Sponsored Products', icon: Sparkles, desc: 'Featured items boosted by verified merchants' },
  { id: 'products_on_sales', label: 'Products on Sales', icon: Tag, desc: 'Items with verified price cuts and discounted listings' },
  { id: 'recommended_products', label: 'Recommended Products', icon: Gift, desc: 'Algorithmically personalized circular products' },
  { id: 'latest_products', label: 'Latest Products', icon: Clock, desc: 'Newly posted pre-loved listings across Nigeria' },
  { id: 'deals_of_the_day', label: 'Deals of the Day', icon: Flame, desc: 'Limited-time daily price slashes with 48h escrow' },
  { id: 'side_ad', label: 'Side-Panel Ad & Escrow Banner', icon: Zap, desc: 'Companion promotional banner with escrow guarantee' },
  { id: 'carbon_ticker', label: 'Planetary Carbon Diversion Ticker', icon: Leaf, desc: 'Live metrics showing CO2 emissions diverted from landfills' },
];

export const CATEGORY_OPTIONS = [
  'Electronics & Gadgets',
  'Phones & Tablets',
  'Fashion & Apparel',
  'Home & Living',
  'Solar & Inverters',
  'Vehicles & Auto Parts',
  'Beauty & Wellness',
  'Books & Hobbies'
];

export const PRESET_BACKGROUND_GRADIENTS = [
  { label: 'Deep Emerald Gradient', value: 'linear-gradient(135deg, #10231D 0%, #154A3A 100%)' },
  { label: 'Mint White Soft Gradient', value: 'linear-gradient(135deg, #EBF8F2 0%, #F5FCF8 100%)' },
  { label: 'Midnight Cyan Gradient', value: 'linear-gradient(135deg, #0C1E24 0%, #163B3F 100%)' },
  { label: 'Forest Bioluminescence', value: 'linear-gradient(135deg, #0F2D24 0%, #1E4638 100%)' },
  { label: 'Sunset Amber Glow', value: 'linear-gradient(135deg, #2B1E14 0%, #452D1A 100%)' },
  { label: 'Solid Emerald Void', value: '#10231D' },
  { label: 'Solid Card Surface', value: '#152A23' },
  { label: 'Transparent Default', value: 'transparent' },
];

export const LandingPageLayoutArranger: React.FC = () => {
  const [activePreset, setActivePreset] = useState<'commercial' | 'clean' | 'eco'>('commercial');
  const [isSaved, setIsSaved] = useState(false);
  const [previewViewport, setPreviewViewport] = useState<'desktop' | 'mobile'>('desktop');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);

  // New Section Modal State
  const [newSectionModalOpen, setNewSectionModalOpen] = useState(false);
  const [newSecName, setNewSecName] = useState('');
  const [newSecType, setNewSecType] = useState<SectionElementType>('product_list_category');
  const [newSecCategory, setNewSecCategory] = useState(CATEGORY_OPTIONS[0]);
  const [newSecTitle, setNewSecTitle] = useState('');
  const [newSecSubtitle, setNewSecSubtitle] = useState('');
  const [newSecBadge, setNewSecBadge] = useState('NEW');
  const [newSecBgType, setNewSecBgType] = useState<'default' | 'solid' | 'gradient'>('default');
  const [newSecBgColor, setNewSecBgColor] = useState('#152A23');
  const [newSecDisplayDesktop, setNewSecDisplayDesktop] = useState<'grid' | 'carousel'>('grid');
  const [newSecDisplayMobile, setNewSecDisplayMobile] = useState<'grid' | 'carousel'>('carousel');
  const [newSecColsDesktop, setNewSecColsDesktop] = useState(4);
  const [newSecColsMobile, setNewSecColsMobile] = useState(1);
  const [newSecShowSeeAll, setNewSecShowSeeAll] = useState(true);
  const [newSecSeeAllText, setNewSecSeeAllText] = useState('See All');
  const [newSecSeeAllUrl, setNewSecSeeAllUrl] = useState('/market');

  const [sections, setSections] = useState<LandingSectionConfig[]>([
    {
      id: 'sec_banners',
      name: 'Hero Responsive Banner Carousel',
      type: 'hero_banner_carousel',
      enabled: true,
      order: 1,
      title: 'Featured Sustainable Promotions',
      subtitle: 'Exclusive discounts on verified circular marketplace electronics and furniture',
      badgeText: 'HERO DEALS',
      bgType: 'default',
      bgColor: 'transparent',
      displayDesktop: 'carousel',
      displayMobile: 'carousel',
      columnsDesktop: 1,
      columnsMobile: 1,
      showSeeAll: true,
      seeAllText: 'Explore Promos',
      seeAllUrl: '/deals'
    },
    {
      id: 'sec_side_ad',
      name: 'Side-Panel Hot Deal & Escrow Card',
      type: 'side_ad',
      enabled: true,
      order: 2,
      title: '100% Escrow Protection Voucher',
      subtitle: 'Use code REUSE2026 for 0% buyer escrow fee on your first order',
      badgeText: 'FREE ESCROW',
      bgType: 'solid',
      bgColor: '#152A23',
      displayDesktop: 'grid',
      displayMobile: 'grid',
      columnsDesktop: 1,
      columnsMobile: 1,
      showSeeAll: false,
      seeAllText: 'Claim Code',
      seeAllUrl: '/escrow'
    },
    {
      id: 'sec_categories',
      name: 'Contrasting Category Showcase Grid',
      type: 'category_showcase',
      enabled: true,
      order: 3,
      title: 'Browse Popular Categories',
      subtitle: 'Explore pre-loved tech, circular fashion, home decors, and refurbished appliances',
      badgeText: 'EXPLORE',
      bgType: 'default',
      bgColor: 'transparent',
      displayDesktop: 'grid',
      displayMobile: 'carousel',
      columnsDesktop: 6,
      columnsMobile: 2,
      showSeeAll: true,
      seeAllText: 'All Categories',
      seeAllUrl: '/categories'
    },
    {
      id: 'sec_deals_day',
      name: 'Deals of the Day',
      type: 'deals_of_the_day',
      enabled: true,
      order: 4,
      title: 'Deals of the Day 🔥',
      subtitle: 'Time-limited circular marketplace deals ending at midnight',
      badgeText: 'HOT DROPS',
      bgType: 'gradient',
      bgColor: 'linear-gradient(135deg, #10231D 0%, #154A3A 100%)',
      displayDesktop: 'carousel',
      displayMobile: 'carousel',
      columnsDesktop: 4,
      columnsMobile: 1,
      showSeeAll: true,
      seeAllText: 'View All Deals',
      seeAllUrl: '/market?filter=deals'
    },
    {
      id: 'sec_trending',
      name: 'Trending Products',
      type: 'trending_products',
      enabled: true,
      order: 5,
      title: 'Trending Electronics & Gadgets',
      subtitle: 'Verified pre-loved items receiving the highest buyer interest today',
      badgeText: 'TRENDING',
      bgType: 'default',
      bgColor: 'transparent',
      displayDesktop: 'grid',
      displayMobile: 'carousel',
      columnsDesktop: 4,
      columnsMobile: 1,
      showSeeAll: true,
      seeAllText: 'See All Trending',
      seeAllUrl: '/market?sort=trending'
    },
    {
      id: 'sec_carbon_ticker',
      name: 'Sustainability Carbon Diversion Ticker',
      type: 'carbon_ticker',
      enabled: true,
      order: 6,
      title: 'Our Planetary Impact',
      subtitle: 'Every second-hand deal diverts ~35kg CO2 from Nigerian landfills',
      badgeText: 'ECO IMPACT',
      bgType: 'default',
      bgColor: 'transparent',
      displayDesktop: 'grid',
      displayMobile: 'grid',
      columnsDesktop: 3,
      columnsMobile: 1,
      showSeeAll: false,
      seeAllText: '',
      seeAllUrl: ''
    },
  ]);

  const getSectionIcon = (type: SectionElementType) => {
    const opt = ELEMENT_TYPE_OPTIONS.find(o => o.id === type);
    if (!opt) return <ShoppingBag className="w-4 h-4 text-emerald-400" />;
    const IconComp = opt.icon;
    return <IconComp className="w-4 h-4 text-brand-400" />;
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const next = [...sections];
    const temp = next[index];
    next[index] = next[index - 1];
    next[index - 1] = temp;
    next.forEach((s, idx) => (s.order = idx + 1));
    setSections(next);
  };

  const handleMoveDown = (index: number) => {
    if (index === sections.length - 1) return;
    const next = [...sections];
    const temp = next[index];
    next[index] = next[index + 1];
    next[index + 1] = temp;
    next.forEach((s, idx) => (s.order = idx + 1));
    setSections(next);
  };

  const handleToggleSection = (id: string) => {
    setSections(prev =>
      prev.map(s => (s.id === id ? { ...s, enabled: !s.enabled } : s))
    );
  };

  const handleUpdateSection = (id: string, updates: Partial<LandingSectionConfig>) => {
    setSections(prev =>
      prev.map(s => (s.id === id ? { ...s, ...updates } : s))
    );
  };

  const handleDeleteSection = (id: string) => {
    if (confirm('Are you sure you want to remove this section from the landing page?')) {
      setSections(prev => {
        const remaining = prev.filter(s => s.id !== id);
        remaining.forEach((s, idx) => (s.order = idx + 1));
        return remaining;
      });
      if (selectedSectionId === id) setSelectedSectionId(null);
    }
  };

  const handleCreateSection = () => {
    if (!newSecName.trim()) return;
    const newSection: LandingSectionConfig = {
      id: `sec_${Date.now()}`,
      name: newSecName.trim(),
      type: newSecType,
      selectedCategory: newSecType === 'product_list_category' ? newSecCategory : undefined,
      enabled: true,
      order: sections.length + 1,
      title: newSecTitle.trim() || newSecName.trim(),
      subtitle: newSecSubtitle.trim(),
      badgeText: newSecBadge.trim(),
      bgType: newSecBgType,
      bgColor: newSecBgColor,
      displayDesktop: newSecDisplayDesktop,
      displayMobile: newSecDisplayMobile,
      columnsDesktop: newSecColsDesktop,
      columnsMobile: newSecColsMobile,
      showSeeAll: newSecShowSeeAll,
      seeAllText: newSecSeeAllText,
      seeAllUrl: newSecSeeAllUrl,
    };

    setSections(prev => [...prev, newSection]);
    setSelectedSectionId(newSection.id);
    setNewSectionModalOpen(false);
    // Reset form
    setNewSecName('');
    setNewSecTitle('');
    setNewSecSubtitle('');
  };

  const applyPreset = (preset: 'commercial' | 'clean' | 'eco') => {
    setActivePreset(preset);
    if (preset === 'commercial') {
      setSections(prev => {
        const orderMap: Record<string, number> = {
          sec_banners: 1,
          sec_side_ad: 2,
          sec_categories: 3,
          sec_deals_day: 4,
          sec_trending: 5,
          sec_carbon_ticker: 6
        };
        return [...prev].sort((a, b) => (orderMap[a.id] || 99) - (orderMap[b.id] || 99)).map((s, idx) => ({
          ...s,
          order: idx + 1,
          enabled: true,
          displayDesktop: s.id === 'sec_banners' || s.id === 'sec_deals_day' ? 'carousel' : 'grid'
        }));
      });
    } else if (preset === 'clean') {
      setSections(prev => {
        const orderMap: Record<string, number> = {
          sec_categories: 1,
          sec_banners: 2,
          sec_trending: 3,
          sec_deals_day: 4,
          sec_carbon_ticker: 5,
          sec_side_ad: 6
        };
        return [...prev].sort((a, b) => (orderMap[a.id] || 99) - (orderMap[b.id] || 99)).map((s, idx) => ({
          ...s,
          order: idx + 1,
          enabled: s.id !== 'sec_side_ad',
          displayDesktop: 'grid'
        }));
      });
    } else if (preset === 'eco') {
      setSections(prev => {
        const orderMap: Record<string, number> = {
          sec_carbon_ticker: 1,
          sec_banners: 2,
          sec_categories: 3,
          sec_trending: 4,
          sec_deals_day: 5,
          sec_side_ad: 6
        };
        return [...prev].sort((a, b) => (orderMap[a.id] || 99) - (orderMap[b.id] || 99)).map((s, idx) => ({
          ...s,
          order: idx + 1,
          enabled: true,
          bgType: s.id === 'sec_carbon_ticker' ? 'gradient' : s.bgType
        }));
      });
    }
  };

  useEffect(() => {
    AdminApiClient.getPublicConfig()
      .then(config => {
        const savedLayout = config?.configs?.landing_layout || config?.settings?.landing_layout;
        if (savedLayout && Array.isArray(savedLayout) && savedLayout.length > 0) {
          setSections(savedLayout);
        }
      })
      .catch(err => console.warn('Could not fetch landing layout from API:', err));
  }, []);

  const handleSave = async () => {
    setIsSaved(true);
    try {
      await AdminApiClient.updateSettings('landing_layout', sections);
    } catch (err: any) {
      console.error('Failed to save layout to PostgreSQL:', err);
    }
    setTimeout(() => setIsSaved(false), 3500);
  };

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-5 rounded-2xl border bg-[#152a23] dark:bg-[#152a23] border-[#22493c] light:bg-white light:border-[#D5E4DC]">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-brand-500/20 text-brand-400 border border-brand-500/30">
              <Layout className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                Landing Page Layout Builder & Widget Arranger
              </h4>
              <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                Create, customize, arrange, and style sections with custom backgrounds, carousels/grids, and "See All" buttons
              </p>
            </div>
          </div>
        </div>

        {/* Presets and Actions */}
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="flex items-center bg-[#10231D] dark:bg-[#10231D] light:bg-[#f1f8f4] p-1 rounded-xl border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC]">
            <button
              onClick={() => applyPreset('commercial')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activePreset === 'commercial'
                  ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                  : 'text-[#90cbb7] hover:text-white'
              }`}
            >
              Layout Commercial
            </button>
            <button
              onClick={() => applyPreset('clean')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activePreset === 'clean'
                  ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                  : 'text-[#90cbb7] hover:text-white'
              }`}
            >
              Layout Clean
            </button>
            <button
              onClick={() => applyPreset('eco')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activePreset === 'eco'
                  ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                  : 'text-[#90cbb7] hover:text-white'
              }`}
            >
              Eco Impact
            </button>
          </div>

          <button
            onClick={() => setNewSectionModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547] border border-brand-500/30 transition-all shadow-sm"
          >
            <Plus className="w-4 h-4 text-brand-400" />
            <span>Add Section</span>
          </button>

          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all"
          >
            <Save className="w-4 h-4" />
            <span>{isSaved ? 'Published Live!' : 'Publish Layout'}</span>
          </button>
        </div>
      </div>

      {isSaved && (
        <div className="flex items-center gap-3 p-3.5 rounded-xl bg-brand-500/20 border border-brand-500/40 text-brand-300 text-xs font-bold animate-fadeIn">
          <CheckCircle2 className="w-4 h-4 text-brand-400" />
          <span>Landing page layout configurations published live to Firestore! Web & mobile clients updated.</span>
        </div>
      )}

      {/* Main Grid: Section List & Editor on Left, Live Viewport on Right */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Left Side: Section List & Customizer */}
        <div className="xl:col-span-5 space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-[#22493c]">
            <h5 className="text-xs font-bold uppercase tracking-wider text-[#90cbb7]">
              Configured Sections ({sections.filter(s => s.enabled).length}/{sections.length} active)
            </h5>
            <span className="text-[11px] text-[#90cbb7]/70">Tap section to customize</span>
          </div>

          <div className="space-y-3">
            {sections.map((section, idx) => {
              const isSelected = selectedSectionId === section.id;
              return (
                <div
                  key={section.id}
                  className={`rounded-2xl border transition-all overflow-hidden ${
                    isSelected
                      ? 'border-brand-500 bg-[#152a23] shadow-glow-sm'
                      : 'border-[#22493c] bg-[#10231D]/80 hover:border-[#2f6654]'
                  }`}
                >
                  {/* Section Row Header */}
                  <div className="p-3.5 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => handleMoveUp(idx)}
                          disabled={idx === 0}
                          className="p-1 rounded text-[#90cbb7] hover:text-white disabled:opacity-20 transition-colors"
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleMoveDown(idx)}
                          disabled={idx === sections.length - 1}
                          className="p-1 rounded text-[#90cbb7] hover:text-white disabled:opacity-20 transition-colors"
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>

                      <div className="p-2 rounded-xl bg-[#22493c] shrink-0">
                        {getSectionIcon(section.type)}
                      </div>

                      <div 
                        className="min-w-0 flex-1 cursor-pointer"
                        onClick={() => setSelectedSectionId(isSelected ? null : section.id)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white truncate">
                            {section.name}
                          </span>
                          {section.badgeText && (
                            <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-brand-500/20 text-brand-300">
                              {section.badgeText}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#90cbb7] truncate flex items-center gap-2 mt-0.5">
                          <span>{section.displayDesktop === 'carousel' ? 'Carousel Rail' : `${section.columnsDesktop} Cols`}</span>
                          <span>•</span>
                          <span className="capitalize">{section.bgType} Bg</span>
                          {section.selectedCategory && (
                            <>
                              <span>•</span>
                              <span className="text-brand-400">{section.selectedCategory}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleToggleSection(section.id)}
                        className={`p-1.5 rounded-lg text-xs font-semibold transition-colors ${
                          section.enabled
                            ? 'text-emerald-400 hover:bg-emerald-500/15'
                            : 'text-[#90cbb7]/40 hover:bg-white/5'
                        }`}
                        title={section.enabled ? 'Enabled on landing page' : 'Disabled / Hidden'}
                      >
                        {section.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>

                      <button
                        onClick={() => setSelectedSectionId(isSelected ? null : section.id)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isSelected ? 'text-brand-400 bg-brand-500/20' : 'text-[#90cbb7] hover:text-white'
                        }`}
                        title="Edit Section Details & Background"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleDeleteSection(section.id)}
                        className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-500/15 transition-colors"
                        title="Delete Section"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Customization Form for Section */}
                  {isSelected && (
                    <div className="p-4 border-t border-[#22493c] bg-[#0c1c17] space-y-4 text-xs">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-[10px] font-bold text-[#90cbb7] uppercase">Section Name / Category Label</label>
                          <input
                            type="text"
                            value={section.name}
                            onChange={(e) => handleUpdateSection(section.id, { name: e.target.value })}
                            className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-[#10231D] border border-[#22493c] text-xs text-white focus:border-brand-500 focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="text-[10px] font-bold text-[#90cbb7] uppercase">Element Type</label>
                          <select
                            value={section.type}
                            onChange={(e) => handleUpdateSection(section.id, { type: e.target.value as SectionElementType })}
                            className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-[#10231D] border border-[#22493c] text-xs text-white focus:border-brand-500 focus:outline-none"
                          >
                            {ELEMENT_TYPE_OPTIONS.map(opt => (
                              <option key={opt.id} value={opt.id}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {section.type === 'product_list_category' && (
                        <div>
                          <label className="text-[10px] font-bold text-[#90cbb7] uppercase">Target Product Category</label>
                          <select
                            value={section.selectedCategory || CATEGORY_OPTIONS[0]}
                            onChange={(e) => handleUpdateSection(section.id, { selectedCategory: e.target.value })}
                            className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-[#10231D] border border-[#22493c] text-xs text-white focus:border-brand-500 focus:outline-none"
                          >
                            {CATEGORY_OPTIONS.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-[10px] font-bold text-[#90cbb7] uppercase">Display Title</label>
                          <input
                            type="text"
                            value={section.title}
                            onChange={(e) => handleUpdateSection(section.id, { title: e.target.value })}
                            className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-[#10231D] border border-[#22493c] text-xs text-white focus:border-brand-500 focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="text-[10px] font-bold text-[#90cbb7] uppercase">Badge Pill Text</label>
                          <input
                            type="text"
                            value={section.badgeText || ''}
                            onChange={(e) => handleUpdateSection(section.id, { badgeText: e.target.value })}
                            placeholder="e.g. HOT, 50% OFF, VERIFIED"
                            className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-[#10231D] border border-[#22493c] text-xs text-white focus:border-brand-500 focus:outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-[10px] font-bold text-[#90cbb7] uppercase">Subtitle / Description</label>
                        <input
                          type="text"
                          value={section.subtitle || ''}
                          onChange={(e) => handleUpdateSection(section.id, { subtitle: e.target.value })}
                          className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-[#10231D] border border-[#22493c] text-xs text-white focus:border-brand-500 focus:outline-none"
                        />
                      </div>

                      {/* Background Customizer: Hex or Gradient */}
                      <div className="p-3 rounded-xl bg-[#10231D] border border-[#22493c] space-y-2.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-white flex items-center gap-1.5">
                            <Palette className="w-3.5 h-3.5 text-brand-400" />
                            <span>Section Background Color / Gradient</span>
                          </label>
                          <div className="flex items-center gap-1 bg-[#0c1c17] p-0.5 rounded-lg border border-[#22493c]">
                            <button
                              type="button"
                              onClick={() => handleUpdateSection(section.id, { bgType: 'default', bgColor: 'transparent' })}
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${section.bgType === 'default' ? 'bg-brand-500 text-forest-void' : 'text-[#90cbb7]'}`}
                            >
                              Default
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateSection(section.id, { bgType: 'solid', bgColor: '#152A23' })}
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${section.bgType === 'solid' ? 'bg-brand-500 text-forest-void' : 'text-[#90cbb7]'}`}
                            >
                              Solid Hex
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateSection(section.id, { bgType: 'gradient', bgColor: PRESET_BACKGROUND_GRADIENTS[0].value })}
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${section.bgType === 'gradient' ? 'bg-brand-500 text-forest-void' : 'text-[#90cbb7]'}`}
                            >
                              Gradient
                            </button>
                          </div>
                        </div>

                        {section.bgType !== 'default' && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              {section.bgType === 'solid' && (
                                <input
                                  type="color"
                                  value={section.bgColor.startsWith('#') ? section.bgColor : '#152A23'}
                                  onChange={(e) => handleUpdateSection(section.id, { bgColor: e.target.value })}
                                  className="w-8 h-8 rounded border border-[#22493c] bg-transparent cursor-pointer"
                                />
                              )}
                              <input
                                type="text"
                                value={section.bgColor}
                                onChange={(e) => handleUpdateSection(section.id, { bgColor: e.target.value })}
                                placeholder="Hex code e.g. #152A23 or CSS gradient"
                                className="flex-1 px-2.5 py-1.5 rounded-lg bg-[#0c1c17] border border-[#22493c] text-xs font-mono text-white focus:border-brand-500"
                              />
                            </div>

                            {/* Preset Gradients & Swatches */}
                            <div className="flex flex-wrap gap-1.5 pt-1">
                              {PRESET_BACKGROUND_GRADIENTS.map((swatch, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => handleUpdateSection(section.id, { 
                                    bgType: swatch.value.includes('gradient') ? 'gradient' : swatch.value === 'transparent' ? 'default' : 'solid',
                                    bgColor: swatch.value 
                                  })}
                                  className="px-2 py-1 rounded text-[10px] border border-[#22493c] hover:border-brand-500/50 text-[#90cbb7] hover:text-white truncate max-w-[140px]"
                                  style={{ background: swatch.value }}
                                >
                                  <span className="bg-black/60 px-1 py-0.2 rounded font-semibold text-[9px] text-white">
                                    {swatch.label}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Display Mode (Rows/Columns vs Carousel) */}
                      <div className="p-3 rounded-xl bg-[#10231D] border border-[#22493c] space-y-2.5">
                        <label className="text-[11px] font-bold text-white flex items-center gap-1.5">
                          <SlidersHorizontal className="w-3.5 h-3.5 text-brand-400" />
                          <span>Presentation & Column Layout</span>
                        </label>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <span className="text-[10px] font-semibold text-[#90cbb7] block mb-1">Desktop Display</span>
                            <div className="flex items-center gap-2">
                              <select
                                value={section.displayDesktop}
                                onChange={(e) => handleUpdateSection(section.id, { displayDesktop: e.target.value as any })}
                                className="flex-1 px-2 py-1 rounded-lg bg-[#0c1c17] border border-[#22493c] text-white text-xs"
                              >
                                <option value="grid">Grid (Rows & Columns)</option>
                                <option value="carousel">Carousel (Scroll Rail)</option>
                              </select>
                              {section.displayDesktop === 'grid' && (
                                <select
                                  value={section.columnsDesktop}
                                  onChange={(e) => handleUpdateSection(section.id, { columnsDesktop: Number(e.target.value) })}
                                  className="w-20 px-2 py-1 rounded-lg bg-[#0c1c17] border border-[#22493c] text-white text-xs"
                                >
                                  <option value={1}>1 Col</option>
                                  <option value={2}>2 Cols</option>
                                  <option value={3}>3 Cols</option>
                                  <option value={4}>4 Cols</option>
                                  <option value={6}>6 Cols</option>
                                </select>
                              )}
                            </div>
                          </div>

                          <div>
                            <span className="text-[10px] font-semibold text-[#90cbb7] block mb-1">Mobile Display</span>
                            <div className="flex items-center gap-2">
                              <select
                                value={section.displayMobile}
                                onChange={(e) => handleUpdateSection(section.id, { displayMobile: e.target.value as any })}
                                className="flex-1 px-2 py-1 rounded-lg bg-[#0c1c17] border border-[#22493c] text-white text-xs"
                              >
                                <option value="carousel">Carousel (Horizontal Rail)</option>
                                <option value="grid">Grid (Vertical Stack)</option>
                              </select>
                              {section.displayMobile === 'grid' && (
                                <select
                                  value={section.columnsMobile}
                                  onChange={(e) => handleUpdateSection(section.id, { columnsMobile: Number(e.target.value) })}
                                  className="w-20 px-2 py-1 rounded-lg bg-[#0c1c17] border border-[#22493c] text-white text-xs"
                                >
                                  <option value={1}>1 Col</option>
                                  <option value={2}>2 Cols</option>
                                </select>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* "See All" Button Customization */}
                      <div className="p-3 rounded-xl bg-[#10231D] border border-[#22493c] space-y-2.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-white flex items-center gap-1.5">
                            <ExternalLink className="w-3.5 h-3.5 text-brand-400" />
                            <span>"See All" Header Action Button</span>
                          </label>
                          <input
                            type="checkbox"
                            checked={section.showSeeAll}
                            onChange={(e) => handleUpdateSection(section.id, { showSeeAll: e.target.checked })}
                            className="w-4 h-4 rounded text-brand-500 accent-brand-500"
                          />
                        </div>

                        {section.showSeeAll && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                            <div>
                              <span className="text-[10px] font-semibold text-[#90cbb7] block mb-0.5">Button Label</span>
                              <input
                                type="text"
                                value={section.seeAllText}
                                onChange={(e) => handleUpdateSection(section.id, { seeAllText: e.target.value })}
                                placeholder="e.g. See All, View More Deals"
                                className="w-full px-2.5 py-1.5 rounded-lg bg-[#0c1c17] border border-[#22493c] text-xs text-white"
                              />
                            </div>
                            <div>
                              <span className="text-[10px] font-semibold text-[#90cbb7] block mb-0.5">Destination URL / Deep Link</span>
                              <input
                                type="text"
                                value={section.seeAllUrl}
                                onChange={(e) => handleUpdateSection(section.id, { seeAllUrl: e.target.value })}
                                placeholder="e.g. /market?category=Electronics"
                                className="w-full px-2.5 py-1.5 rounded-lg bg-[#0c1c17] border border-[#22493c] text-xs font-mono text-white"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Side: Live Viewport Landing Simulation */}
        <div className="xl:col-span-7 space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-[#22493c]">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-brand-400 animate-pulse"></span>
              <h5 className="text-xs font-bold uppercase tracking-wider text-[#90cbb7]">
                Live Viewport Landing Simulation
              </h5>
            </div>

            <div className="flex items-center bg-[#10231D] p-1 rounded-xl border border-[#22493c]">
              <button
                onClick={() => setPreviewViewport('desktop')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                  previewViewport === 'desktop'
                    ? 'bg-brand-500 text-forest-void'
                    : 'text-[#90cbb7] hover:text-white'
                }`}
              >
                <Monitor className="w-3.5 h-3.5" />
                <span>Desktop (1280px)</span>
              </button>
              <button
                onClick={() => setPreviewViewport('mobile')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                  previewViewport === 'mobile'
                    ? 'bg-brand-500 text-forest-void'
                    : 'text-[#90cbb7] hover:text-white'
                }`}
              >
                <Smartphone className="w-3.5 h-3.5" />
                <span>Mobile (375px)</span>
              </button>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-[#0a1612] border border-[#22493c] flex items-center justify-center min-h-[640px]">
            {previewViewport === 'desktop' ? (
              /* Desktop Mockup */
              <div className="w-full bg-[#10221c] rounded-xl border border-[#1f3d32] overflow-hidden shadow-2xl">
                {/* Desktop Mini Header */}
                <div className="px-5 py-3 bg-[#0d1e18] border-b border-[#1f3d32] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#d5f0e1] flex items-center justify-center text-[10px] text-[#0d9e5d] font-bold">
                      🌿
                    </div>
                    <span className="text-sm font-extrabold text-white">ReUseIt</span>
                  </div>
                  <div className="w-64 h-7 bg-[#152a23] rounded-full border border-[#22493c] px-3 flex items-center text-[11px] text-[#90cbb7]">
                    Search electronics, fashion, escrow deals...
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#90cbb7]">
                    <span className="text-brand-400 font-bold">Deals 🔥</span>
                    <button className="px-2.5 py-1 rounded-full bg-brand-500 text-forest-void text-[10px] font-bold">
                      Sell Item
                    </button>
                  </div>
                </div>

                {/* Desktop Viewport Body */}
                <div className="p-5 space-y-5 max-h-[600px] overflow-y-auto">
                  {sections
                    .filter(s => s.enabled)
                    .map(sec => {
                      const bgStyle = sec.bgType === 'gradient' || sec.bgType === 'solid'
                        ? { background: sec.bgColor }
                        : {};

                      return (
                        <div 
                          key={sec.id} 
                          className="rounded-2xl p-4 border border-[#22493c]/60 transition-all space-y-3"
                          style={bgStyle}
                        >
                          {/* Section Header with Title & See All */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getSectionIcon(sec.type)}
                              <h5 className="font-bold text-sm text-white">{sec.title}</h5>
                              {sec.badgeText && (
                                <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-brand-500/20 text-brand-300">
                                  {sec.badgeText}
                                </span>
                              )}
                            </div>
                            {sec.showSeeAll && (
                              <a 
                                href="#" 
                                onClick={(e) => e.preventDefault()}
                                className="text-xs font-bold text-brand-400 hover:underline flex items-center gap-1"
                              >
                                <span>{sec.seeAllText || 'See All'}</span>
                                <ChevronRight className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>

                          {/* Section Content Visualization */}
                          {sec.type === 'hero_banner_carousel' ? (
                            <div className="h-44 rounded-2xl bg-gradient-to-r from-[#0f3e30] via-[#154a3a] to-[#0d2a21] p-5 flex flex-col justify-between border border-brand-500/30">
                              <div className="space-y-1">
                                <span className="px-2 py-0.5 rounded-md bg-brand-500 text-forest-void text-[9px] font-extrabold">
                                  VERIFIED ESCROW PROMO
                                </span>
                                <h3 className="text-base font-extrabold text-white">
                                  Circular Tech & Green Living Marketplace
                                </h3>
                                <p className="text-xs text-[#90cbb7] max-w-sm">
                                  {sec.subtitle || 'Shop with 48h funds safety & instant Nigerian bank payouts.'}
                                </p>
                              </div>
                              <div className="flex items-center justify-between">
                                <button className="px-3 py-1.5 rounded-full bg-brand-500 text-forest-void font-bold text-xs">
                                  Shop Now
                                </button>
                                <div className="flex gap-1">
                                  <div className="w-4 h-1.5 rounded-full bg-brand-400"></div>
                                  <div className="w-1.5 h-1.5 rounded-full bg-white/40"></div>
                                  <div className="w-1.5 h-1.5 rounded-full bg-white/40"></div>
                                </div>
                              </div>
                            </div>
                          ) : sec.type === 'category_showcase' ? (
                            <div className="grid grid-cols-6 gap-2">
                              {[
                                { name: 'Phones', bg: '#D1FAE5', color: '#065F46', icon: '📱' },
                                { name: 'Laptops', bg: '#DBEAFE', color: '#1E40AF', icon: '💻' },
                                { name: 'Fashion', bg: '#FCE7F3', color: '#9D174D', icon: '👗' },
                                { name: 'Solar', bg: '#FEF3C7', color: '#92400E', icon: '☀️' },
                                { name: 'Home', bg: '#E0E7FF', color: '#3730A3', icon: '🛋️' },
                                { name: 'Books', bg: '#FEE2E2', color: '#991B1B', icon: '📚' },
                              ].map((c, i) => (
                                <div key={i} className="p-2.5 rounded-xl flex flex-col items-center justify-center gap-1 shadow-sm" style={{ backgroundColor: c.bg, color: c.color }}>
                                  <span className="text-lg">{c.icon}</span>
                                  <span className="text-[10px] font-bold">{c.name}</span>
                                </div>
                              ))}
                            </div>
                          ) : sec.type === 'carbon_ticker' ? (
                            <div className="grid grid-cols-3 gap-3">
                              <div className="p-3 rounded-xl bg-[#10231D] border border-[#22493c] text-center">
                                <span className="text-lg font-black text-brand-400">1,842 kg</span>
                                <span className="text-[10px] text-[#90cbb7] block">CO2 Diverted</span>
                              </div>
                              <div className="p-3 rounded-xl bg-[#10231D] border border-[#22493c] text-center">
                                <span className="text-lg font-black text-teal-300">4,290</span>
                                <span className="text-[10px] text-[#90cbb7] block">Items Saved</span>
                              </div>
                              <div className="p-3 rounded-xl bg-[#10231D] border border-[#22493c] text-center">
                                <span className="text-lg font-black text-amber-300">₦24.8M</span>
                                <span className="text-[10px] text-[#90cbb7] block">Escrow Protected</span>
                              </div>
                            </div>
                          ) : (
                            /* Generic Product Shelf (Grid or Carousel) */
                            <div className={sec.displayDesktop === 'carousel' ? 'flex gap-3 overflow-x-auto pb-2' : `grid grid-cols-${sec.columnsDesktop || 4} gap-3`}>
                              {[1, 2, 3, 4].map(num => (
                                <div key={num} className="p-2.5 rounded-xl bg-[#10231D] border border-[#22493c] space-y-2 min-w-[130px] flex-1">
                                  <div className="w-full h-20 rounded-lg bg-[#152A23] flex items-center justify-center text-xs text-[#90cbb7]">
                                    {sec.selectedCategory ? `${sec.selectedCategory} #${num}` : `Product Item #${num}`}
                                  </div>
                                  <div className="space-y-0.5">
                                    <div className="text-[11px] font-bold text-white truncate">
                                      {sec.type === 'deals_of_the_day' ? 'Flash Deal Item' : sec.type === 'trending_products' ? 'Trending Gadget' : 'Verified Pre-Loved'}
                                    </div>
                                    <div className="flex items-center justify-between text-[11px]">
                                      <span className="text-brand-400 font-extrabold">₦{num * 25},000</span>
                                      <span className="text-[9px] text-[#90cbb7]">Escrow</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            ) : (
              /* Mobile Mockup (375px) */
              <div className="w-[340px] bg-[#0c1a15] rounded-3xl border-4 border-[#1f3d32] overflow-hidden shadow-2xl">
                {/* Mobile Top Bar */}
                <div className="px-4 py-2.5 bg-[#08120e] border-b border-[#1f3d32] flex items-center justify-between">
                  <span className="text-[11px] font-extrabold text-white">ReUseIt Mobile</span>
                  <div className="w-2 h-2 rounded-full bg-brand-400"></div>
                </div>

                {/* Mobile Scrollable Viewport */}
                <div className="p-3 space-y-3 max-h-[520px] overflow-y-auto">
                  {sections
                    .filter(s => s.enabled)
                    .map(sec => {
                      const bgStyle = sec.bgType === 'gradient' || sec.bgType === 'solid'
                        ? { background: sec.bgColor }
                        : {};

                      return (
                        <div key={sec.id} className="p-3 rounded-2xl border border-[#22493c]/60 space-y-2" style={bgStyle}>
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-xs text-white">{sec.title}</span>
                            {sec.showSeeAll && (
                              <span className="text-[10px] text-brand-400 font-bold">{sec.seeAllText || 'See All'}</span>
                            )}
                          </div>

                          {sec.type === 'hero_banner_carousel' ? (
                            <div className="h-28 rounded-xl bg-gradient-to-r from-[#0f3e30] to-[#0d2a21] p-3 flex flex-col justify-between">
                              <span className="text-[8px] font-bold text-brand-300">100% ESCROW PROTECTED</span>
                              <div className="text-xs font-bold text-white leading-tight">Featured Pre-Loved Tech</div>
                              <button className="self-start px-2 py-0.5 rounded bg-brand-500 text-forest-void text-[9px] font-bold">
                                Shop
                              </button>
                            </div>
                          ) : sec.type === 'category_showcase' ? (
                            <div className="grid grid-cols-3 gap-1.5">
                              {[
                                { name: 'Phones', bg: '#D1FAE5', color: '#065F46' },
                                { name: 'Laptops', bg: '#DBEAFE', color: '#1E40AF' },
                                { name: 'Solar', bg: '#FEF3C7', color: '#92400E' },
                              ].map((c, i) => (
                                <div key={i} className="p-2 rounded-lg text-center text-[9px] font-bold" style={{ background: c.bg, color: c.color }}>
                                  {c.name}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex gap-2 overflow-x-auto pb-1">
                              {[1, 2, 3].map(n => (
                                <div key={n} className="p-2 rounded-lg bg-[#10231D] min-w-[90px] border border-[#22493c]">
                                  <div className="w-full h-12 rounded bg-[#152A23] mb-1"></div>
                                  <div className="text-[9px] font-bold text-white truncate">Item #{n}</div>
                                  <div className="text-[9px] text-brand-400 font-extrabold">₦{n * 15}k</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal: Create New Landing Page Section */}
      {newSectionModalOpen && (
        <Modal
          isOpen={newSectionModalOpen}
          onClose={() => setNewSectionModalOpen(false)}
          title="Add New Landing Page Section"
          subtitle="Define element type, custom background, column/carousel layout, and see-all navigation"
          maxWidth="lg"
        >
          <div className="space-y-4 text-xs">
            <div>
              <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                Section Name / Internal Label:
              </label>
              <input
                type="text"
                value={newSecName}
                onChange={(e) => {
                  setNewSecName(e.target.value);
                  if (!newSecTitle) setNewSecTitle(e.target.value);
                }}
                placeholder="e.g. Flash Solar Deals or Curated Designer Sneakers"
                className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                  Element Widget Type:
                </label>
                <select
                  value={newSecType}
                  onChange={(e) => setNewSecType(e.target.value as SectionElementType)}
                  className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
                >
                  {ELEMENT_TYPE_OPTIONS.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {newSecType === 'product_list_category' ? (
                <div>
                  <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                    Select Target Category:
                  </label>
                  <select
                    value={newSecCategory}
                    onChange={(e) => setNewSecCategory(e.target.value)}
                    className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
                  >
                    {CATEGORY_OPTIONS.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                    Badge Pill Text (Optional):
                  </label>
                  <input
                    type="text"
                    value={newSecBadge}
                    onChange={(e) => setNewSecBadge(e.target.value)}
                    placeholder="e.g. HOT, 50% OFF, VERIFIED"
                    className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                  Customer-Facing Section Title:
                </label>
                <input
                  type="text"
                  value={newSecTitle}
                  onChange={(e) => setNewSecTitle(e.target.value)}
                  placeholder="e.g. Verified Solar Equipment Under Escrow"
                  className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                  Subtitle / Description:
                </label>
                <input
                  type="text"
                  value={newSecSubtitle}
                  onChange={(e) => setNewSecSubtitle(e.target.value)}
                  placeholder="e.g. Certified batteries with 48h testing window"
                  className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
                />
              </div>
            </div>

            {/* Background Color / Gradient Setup */}
            <div className="p-3.5 rounded-xl bg-[#10231D] border border-[#22493c] space-y-2.5">
              <label className="block text-xs font-semibold text-white">
                Background Styling (Hex or Gradient):
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={newSecBgType}
                  onChange={(e) => setNewSecBgType(e.target.value as any)}
                  className="px-2.5 py-1.5 rounded-lg bg-[#152a23] border border-[#22493c] text-white text-xs"
                >
                  <option value="default">Default Background</option>
                  <option value="solid">Solid Hex Color</option>
                  <option value="gradient">Gradient Background</option>
                </select>
                {newSecBgType !== 'default' && (
                  <input
                    type="text"
                    value={newSecBgColor}
                    onChange={(e) => setNewSecBgColor(e.target.value)}
                    placeholder="#152A23 or linear-gradient(...)"
                    className="flex-1 px-2.5 py-1.5 rounded-lg bg-[#152a23] border border-[#22493c] text-white text-xs font-mono"
                  />
                )}
              </div>
            </div>

            {/* Presentation Layout (Carousel vs Grid) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                  Desktop Display:
                </label>
                <select
                  value={newSecDisplayDesktop}
                  onChange={(e) => setNewSecDisplayDesktop(e.target.value as any)}
                  className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white"
                >
                  <option value="grid">Grid (Rows & Columns)</option>
                  <option value="carousel">Carousel (Horizontal Rail)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                  Mobile Display:
                </label>
                <select
                  value={newSecDisplayMobile}
                  onChange={(e) => setNewSecDisplayMobile(e.target.value as any)}
                  className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white"
                >
                  <option value="carousel">Carousel (Horizontal Rail)</option>
                  <option value="grid">Grid (Vertical Stack)</option>
                </select>
              </div>
            </div>

            {/* "See All" Action Link */}
            <div className="p-3.5 rounded-xl bg-[#10231D] border border-[#22493c] space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-white">Include "See All" Action Button</label>
                <input
                  type="checkbox"
                  checked={newSecShowSeeAll}
                  onChange={(e) => setNewSecShowSeeAll(e.target.checked)}
                  className="w-4 h-4 rounded text-brand-500 accent-brand-500"
                />
              </div>

              {newSecShowSeeAll && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <input
                    type="text"
                    value={newSecSeeAllText}
                    onChange={(e) => setNewSecSeeAllText(e.target.value)}
                    placeholder="e.g. See All"
                    className="p-2 rounded-lg bg-[#152a23] border border-[#22493c] text-white text-xs"
                  />
                  <input
                    type="text"
                    value={newSecSeeAllUrl}
                    onChange={(e) => setNewSecSeeAllUrl(e.target.value)}
                    placeholder="e.g. /market?category=Solar"
                    className="p-2 rounded-lg bg-[#152a23] border border-[#22493c] text-white text-xs font-mono"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#22493c]">
              <button
                type="button"
                onClick={() => setNewSectionModalOpen(false)}
                className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateSection}
                disabled={!newSecName.trim()}
                className="px-5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 disabled:opacity-50 shadow-glow-sm"
              >
                Create Section
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
